import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CommentModal from './CommentModal';
import { config } from '../../config';

const formatDate = (date) => {
  const now = new Date();
  const postDate = new Date(date);
  const diffMs = now - postDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'A l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours} h`;
  if (diffDays === 1) return 'Hier';
  return postDate.toLocaleDateString('fr-FR');
};

export default function PostCard({
  post,
  currentUserId,
  onLike,
  onDelete,
  onShare
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const location = useLocation();

  const authorId = post.id_utilisateur;
  const viewerId = currentUserId || currentUserId === 0
    ? Number(currentUserId)
    : null;
  const isOwner = Number(authorId) === viewerId;
  const mediaUrl = config.normalizePostMedia(post.media_url);
  const profilePhotoUrl = config.normalizeProfilePhoto(post.photo_profil);
  const roleRoot = location.pathname.split('/').slice(0, 2).join('/');
  const authorPath = authorId ? `${roleRoot}/auteur/${authorId}` : null;

  const handleCommentAdded = () => {
    setCommentCount((prev) => prev + 1);
  };

  const renderAuthor = () => {
    const content = (
      <>
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt={`${post.prenom} ${post.nom}`}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border border-gray-200"
          />
        ) : (
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-base md:text-xl">
            {post.prenom?.[0]}{post.nom?.[0]}
          </div>
        )}
        <div>
          <p className="font-semibold text-gray-800 text-sm md:text-lg group-hover:text-blue-700">
            {post.prenom} {post.nom}
          </p>
          <p className="text-xs text-gray-400">
            {formatDate(post.created_at)}
          </p>
        </div>
      </>
    );

    if (!authorPath) {
      return <div className="flex items-center gap-3">{content}</div>;
    }

    return (
      <Link
        to={authorPath}
        className="group flex items-center gap-3 rounded-md pr-2 transition hover:bg-blue-50"
        title="Voir les publications de cet utilisateur"
      >
        {content}
      </Link>
    );
  };

  const renderMedia = () => {
    if (!mediaUrl) return null;

    switch (post.type_media) {
      case 'image':
        return (
          <div className="relative bg-gray-100 overflow-hidden">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse w-8 h-8 rounded-full bg-gray-300" />
              </div>
            )}
            <img
              src={mediaUrl}
              alt="Post"
              className="w-full max-h-[600px] object-contain"
              onLoad={() => setImageLoaded(true)}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
          </div>
        );
      case 'video':
        return (
          <video
            src={mediaUrl}
            controls
            className="w-full max-h-[600px]"
            controlsList="nodownload"
          />
        );
      case 'pdf':
        return (
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-gray-100 p-4"
            style={{ fontFamily: "'Patrick Hand', 'Caveat', cursive" }}
          >
            <span className="rounded bg-red-600 px-2 py-1 text-xs font-bold text-white">
              PDF
            </span>
            <div>
              <p className="font-medium text-base md:text-lg">Document PDF</p>
              <p className="text-xs text-gray-500">Cliquez pour ouvrir</p>
            </div>
          </a>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className="bg-white border-b border-gray-200 w-full"
        style={{ fontFamily: "'Patrick Hand', 'Caveat', 'Comic Neue', cursive" }}
      >
        <div className="p-4 flex justify-between items-start">
          {renderAuthor()}
          {isOwner && (
            <button
              onClick={() => onDelete(post.id_post)}
              className="text-gray-400 hover:text-red-500 transition text-xl leading-none px-2"
            >
              ...
            </button>
          )}
        </div>

        {post.contenu && (
          <div className="px-4 pb-3">
            <p className="text-gray-700 whitespace-pre-wrap text-base md:text-xl leading-relaxed">
              {post.contenu}
            </p>
          </div>
        )}

        {renderMedia()}

        <div className="px-4 py-3 border-t border-gray-100 flex gap-6">
          <button
            onClick={() => onLike(post.id_post)}
            className={`flex items-center gap-2 transition text-sm md:text-base ${post.user_liked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}
          >
            <span className="font-medium">
              {post.user_liked ? 'Aime' : 'Aimer'}
            </span>
            <span className="font-medium">{post.likes || 0}</span>
          </button>

          <button
            onClick={() => setShowComments(true)}
            className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition text-sm md:text-base"
          >
            <span className="font-medium">Commentaires</span>
            <span className="font-medium">{commentCount}</span>
          </button>

          <button
            onClick={() => onShare(post)}
            className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition text-sm md:text-base"
          >
            <span className="font-medium">Partager</span>
          </button>
        </div>
      </div>

      <CommentModal
        isOpen={showComments}
        onClose={() => setShowComments(false)}
        post={post}
        currentUserId={currentUserId}
        onCommentAdded={handleCommentAdded}
      />
    </>
  );
}
